import type { FunctionComponent } from "react";
import styled, { keyframes } from "styled-components";
import { GrayColors } from "@smartsuite/types";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

// Same visual shell as SubConditionItem's StyledWrapper -- the skeleton must
// occupy the exact footprint the real box will take so the swap causes no
// layout shift.
const StyledBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 15px;
  background-color: ${GrayColors["G-0"]};
  border: 1px solid ${GrayColors["G-1-5"]};
  border-radius: 8px;
`;

// Each bar is vertically centered inside the text slot it stands in for --
// caption (size "s", 15px line) and value (size "m", 20px line).
// Opacity-only animation (no width/height animation) per the design
// guidelines; static bars still read as a placeholder when the user prefers
// reduced motion.
const StyledBar = styled.div<{
  $width: number;
  $height: number;
  $slot: number;
}>`
  width: ${({ $width }) => $width}px;
  max-width: 100%;
  height: ${({ $height }) => $height}px;
  margin: ${({ $slot, $height }) => ($slot - $height) / 2}px 0;
  border-radius: 4px;
  background-color: ${GrayColors["G-1"]};
  animation: ${pulse} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

interface SubConditionItemSkeletonProps {
  testId?: string;
}

/**
 * Loading placeholder shaped like one SubConditionItem (caption-over-value)
 * -- shown while a lazy engine fetch backing the box is in flight. A
 * settled-empty fetch renders nothing instead, so the skeleton can never get
 * stuck.
 */
export const SubConditionItemSkeleton: FunctionComponent<
  SubConditionItemSkeletonProps
> = ({ testId }) => (
  <StyledBox data-testid={testId}>
    <StyledBar $width={72} $height={9} $slot={15} />
    <StyledBar $width={210} $height={12} $slot={20} />
  </StyledBox>
);
